import { Component, computed, effect, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { IconField } from 'primeng/iconfield';
import { InputTextModule } from 'primeng/inputtext';
import { InputIcon } from 'primeng/inputicon';
import { FloatLabel } from 'primeng/floatlabel';
import { Button } from 'primeng/button';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ClientListComponent } from '../../components/client-list/client-list.component';
import { ClientFacadeService } from '../../services/client-facade.service';
import { PageWrapperComponent } from '../../../../shared/pages/page-wrapper/page-wrapper.component';
import { AuthStore } from '../../../../auth/services/auth.store';
import { MessageService } from '../../../../shared/services/message.service';
import { UpdateClientDto } from '../../dto/updateClientDto';
import { NgIf } from '@angular/common';
import { ConfirmDeleteClientModalComponent } from '../../components/confirm-delete-client-modal/confirm-delete-client-modal.component';
import { ModalService } from '../../../../shared/services/modal.service';
import {
  ClientFormComponent,
  uniqueClientNameValidator,
} from '../../components/client-form/client-form.component';
import { EditClientModalComponent } from '../../components/edit-client-modal/edit-client-modal.component';
import { ClientDto, PageClientDto } from '../../../../api';
import { Card } from 'primeng/card';
import { debounceTime, of, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, finalize, switchMap } from 'rxjs/operators';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { parseTableLazyLoadEvent } from '../../../../shared/utils/table-lazy-load';

@Component({
  selector: 'app-clients-page',
  standalone: true,
  imports: [
    IconField,
    InputIcon,
    InputTextModule,
    FloatLabel,
    FormsModule,
    Button,
    TableModule,
    TooltipModule,
    ClientListComponent,
    PageWrapperComponent,
    ReactiveFormsModule,
    NgIf,
    Card,
  ],
  templateUrl: './clients-page.component.html',
  styleUrl: './clients-page.component.css',
})
export class ClientsPageComponent {
  private readonly clientService = inject(ClientFacadeService);
  private readonly messageService = inject(MessageService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly authStore = inject(AuthStore);
  private readonly modalService = inject(ModalService);

  clientSearchSubject: Subject<string> = new Subject<string>();

  clients = signal<Array<ClientDto>>([]);
  totalRecords = signal<number>(0);
  rows = signal<number>(5);
  loading = signal(false);
  currentPage = signal<number>(0);

  readonly isOwner = this.authStore.isOwner;

  newClientFormGroup: FormGroup;

  get name(): AbstractControl {
    return this.newClientFormGroup.get('name')!;
  }

  readonly activeWorkspaceId = computed(() => this.authStore.defaultWorkspace()?.id!);
  readonly isLoggedIn = computed(() => this.authStore.isLoggedIn());

  constructor() {
    this.newClientFormGroup = new FormGroup({
      name: new FormControl('', [Validators.required, uniqueClientNameValidator([], undefined)]),
    });

    effect(() => {
      if (!this.isLoggedIn()) return;
      const _refresh = this.clientService.refresh();
    });

    this.clientSearchSubject
      .pipe(
        debounceTime(300),
        filter((term: string) => term.length > 2 || term.length === 0),
        switchMap((term: string) => {
          const workspaceId = this.activeWorkspaceId();
          if (!workspaceId) {
            return of(this.emptyPage());
          }
          return this.clientService.searchClients(workspaceId, term);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((page: PageClientDto) => {
        this.clients.set(page.content ?? []);
        this.totalRecords.set(page.totalElements ?? 0);
      });
  }

  private emptyPage(): PageClientDto {
    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      number: 0,
      size: 0,
    };
  }

  protected lazyLoadClients = (event: TableLazyLoadEvent) => {
    const { page, size, sort } = parseTableLazyLoadEvent(
      event,
      (field, direction) => `${field},${direction}`,
    );

    this.currentPage.set(page);
    this.rows.set(size);

    this.loadClients(page, size, sort);
  };

  /**
   * Aktualizuje validator dla szybkiego dodawania klienta
   */
  private updateQuickAddValidator(): void {
    const nameControl = this.newClientFormGroup.get('name');
    if (nameControl) {
      nameControl.setValidators([
        Validators.required,
        uniqueClientNameValidator(this.clients(), undefined),
      ]);
      nameControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Szybkie dodawanie klienta z głównego widoku (tylko nazwa)
   */
  protected onAddClient(): void {
    const workspaceId = this.activeWorkspaceId();
    if (!workspaceId) return;

    if (this.newClientFormGroup.invalid) {
      this.newClientFormGroup.markAllAsTouched();
      return;
    }

    const newClient: ClientDto = {
      name: this.newClientFormGroup.get('name')?.value?.trim(),
      email: undefined,
      phone: undefined,
      note: undefined,
    };

    this.clientService.addClient(workspaceId, newClient).subscribe({
      next: (createdClient: ClientDto) => {
        this.messageService.showSuccess(
          'Sukces',
          `Pomyślnie dodano klienta ${createdClient.name}.`,
        );
        this.loadClients();
        this.newClientFormGroup.reset();
        this.updateQuickAddValidator();
      },
      error: (err: any) => {
        console.error('Error adding client:', err);
        this.messageService.showError(
          'Błąd dodawania klienta',
          'Wystąpił błąd podczas dodawania klienta.',
        );
      },
    });
  }

  /**
   * Otwiera modal edycji klienta
   */
  protected onEditClient = (client: ClientDto): void => {
    const workspaceId = this.activeWorkspaceId();
    if (!workspaceId) return;

    this.modalService.open(EditClientModalComponent, {
      client: client,
      clients: this.clients(),
      onConfirm: (formComponent: ClientFormComponent) =>
        this.confirmEdit(workspaceId, client.id!, formComponent),
    });
  };

  /**
   * Wykonuje faktyczną edycję klienta
   */
  private confirmEdit(
    workspaceId: string,
    clientId: string,
    formComponent: ClientFormComponent,
  ): void {
    const updatedData: UpdateClientDto = formComponent.getData();

    this.clientService.editClient(workspaceId, clientId, updatedData).subscribe({
      next: (updatedClient: ClientDto) => {
        this.clients.update((clients) =>
          clients.map((client) => (client.id === updatedClient.id ? updatedClient : client)),
        );

        this.modalService.close();

        this.messageService.showSuccess(
          'Sukces',
          `Pomyślnie zaktualizowano klienta ${updatedClient.name}.`,
        );

        this.updateQuickAddValidator();
      },
      error: (err: any) => {
        console.error('Error updating client:', err);
        this.messageService.showError(
          'Błąd aktualizacji klienta',
          'Wystąpił błąd podczas aktualizacji klienta.',
        );
      },
    });
  }

  /**
   * Otwiera modal z potwierdzeniem usunięcia klienta
   */
  protected onDeleteClient = (client: ClientDto): void => {
    this.modalService.open(ConfirmDeleteClientModalComponent, {
      client,
      onConfirm: (clientToDelete: ClientDto) => this.confirmDeleteClient(clientToDelete),
    });
  };

  /**
   * Potwierdza i wykonuje usunięcie klienta
   */
  private confirmDeleteClient(client: ClientDto): void {
    const workspaceId = this.activeWorkspaceId();
    if (!workspaceId) return;

    this.clientService.deleteClient(workspaceId, client.id!).subscribe({
      next: () => {
        this.messageService.showSuccess(
          'Usuwanie klienta',
          `Pomyślnie usunięto klienta ${client.name}`,
        );
        this.clientService.triggerRefresh();
        this.loadClients();
        this.updateQuickAddValidator();
      },
      error: (error) => {
        this.errorHandler.handleError(
          error,
          false,
          `Wystąpił błąd podczas usuwania klienta ${client.name}`,
        );
        console.error('Error deleting client:', error);
      },
    });
  }

  protected onClientSearch($event: Event) {
    let value = ($event.target as HTMLInputElement).value;
    this.clientSearchSubject.next(value);
  }

  private loadClients(
    page: number = this.currentPage(),
    size: number = 20,
    sort: string[] = [],
  ): void {
    this.loading.set(true);

    this.clientService
      .getPageClients(this.activeWorkspaceId()!, { page, size, sort })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe((response) => {
        const newPage = page > 0 && response.content?.length === 0 ? page - 1 : page;
        this.clients.set(response.content ?? []);
        this.totalRecords.set(response.totalElements ?? 0);
        this.currentPage.set(newPage);
      });
  }
}
